import React from "react";
import { Box, Heading, Text, VStack, Code, Button } from "@chakra-ui/react";
import * as Sentry from "@sentry/react";

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

/**
 * Top-level React error boundary.
 *
 * Catches render-time errors from its subtree, reports them to Sentry, and
 * shows a fallback screen with a reload button (plus the stack in dev).
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    /**
     * Derive the error state from a thrown error so the fallback UI renders.
     *
     * @param error - The error thrown by a descendant component.
     * @returns The next state flagging the error.
     */
    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Sentry.captureException(error, {
            contexts: { react: { componentStack: errorInfo?.componentStack } },
        });
    }

    /** Reload the whole page to recover from the error state. */
    handleReload = (): void => {
        globalThis.location.reload();
    };

    /** Render the children, or the error fallback when an error was caught. */
    render(): React.ReactNode {
        if (this.state.hasError) {
            const showStack = process.env.NODE_ENV !== "production" && this.state.error;
            return (
                <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" p={8} bg="gray.50">
                    <VStack spacing={5} maxW="2xl" align="stretch">
                        <Heading as="h1" size="lg" color="red.600" textAlign="center">
                            Une erreur s&apos;est produite
                        </Heading>
                        <Text textAlign="center">
                            L&apos;application a rencontré une erreur inattendue. Rechargez la page pour réessayer.
                        </Text>
                        <Button colorScheme="blue" onClick={this.handleReload} alignSelf="center">
                            Recharger la page
                        </Button>
                        {showStack && (
                            <Code p={4} fontSize="xs" maxH="40vh" overflow="auto" whiteSpace="pre-wrap" colorScheme="red">
                                {String(this.state.error?.stack || this.state.error)}
                            </Code>
                        )}
                    </VStack>
                </Box>
            );
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
